import React, { useState, useEffect, useCallback, useRef } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import useTripCreation from "@/stores/new-trip/useNewTrip";
import { AutocompleteResult } from "@/types";
import { debounce } from "lodash";
import api from "@/config/axiosConfig";

interface DepartureLocationStepProps {
  getFlagImage: (countryName: string) => string;
}

const DepartureLocationStep: React.FC<DepartureLocationStepProps> = ({
  getFlagImage,
}) => {
  const {
    inputValue: storeInputValue,
    leavingFrom: storeLeavingFrom,
    setInputValue,
    setLeavingFrom,
    nextStep,
  } = useTripCreation();

  const inputRef = useRef<HTMLInputElement>(null);
  const [inputValue, setLocalInputValue] = useState(storeInputValue);
  const [autocompleteResults, setAutocompleteResults] = useState<
    AutocompleteResult[]
  >([]);
  const [autocompleteLoading, setAutocompleteLoading] = useState(false);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setLocalInputValue(newValue);
    fetchPlaces(newValue);
  };

  const fetchPlaces = useCallback(
    debounce(async (input: string) => {
      setAutocompleteLoading(true);

      if (!input || input.length <= 2 || input.length > 100) {
        setAutocompleteResults([]);
        setAutocompleteLoading(false);
        return;
      }

      try {
        const response = await api.places.get("/autocomplete", {
          params: { q: input },
        });
        setAutocompleteResults(response.data);
        setAutocompleteLoading(false);
      } catch (error) {
        console.error("Error fetching places:", error);
        setAutocompleteResults([]);
        setAutocompleteLoading(false);
      }
    }, 300),
    []
  );

  const handleAutocompleteSelect = (result: AutocompleteResult) => {
    setLocalInputValue(result.name);
    setInputValue(result.name);
    setLeavingFrom(result);
    setAutocompleteResults([]);
    nextStep();
  };

  const handleNext = () => {
    setInputValue(inputValue);
    nextStep();
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (
        autocompleteResults &&
        autocompleteResults.length > 0 &&
        autocompleteResults[0]
      ) {
        handleAutocompleteSelect(autocompleteResults[0]);
      } else {
        handleNext();
      }
    }
  };

  return (
    <div className="flex flex-col items-center justify-center space-y-8">
      <h2 className="text-2xl font-semibold text-neutral-800">
        Where are you leaving from?
      </h2>
      <div className="relative w-full">
        <div className="relative group">
          <Input
            ref={inputRef}
            id="leavingFrom"
            value={inputValue}
            onChange={handleChange}
            onKeyDown={handleKeyPress}
            placeholder="Enter your departure location"
            className="w-full pr-10 rounded-2xl py-6 pl-6 text-center text-md bg-white shadow-sm border border-gray-200 transition-all duration-200 focus:shadow-md"
            autoComplete="off"
          />
          <Button
            onClick={handleNext}
            className="absolute top-1/2 -translate-y-1/2 right-3 px-3 flex items-center justify-center bg-transparent hover:bg-transparent group-hover:translate-x-1 transition-transform duration-200"
          >
            <ArrowRight className="h-5 w-5 text-indigo-600" />
          </Button>
        </div>
        {(autocompleteResults?.length > 0 || autocompleteLoading) && (
          <AutocompleteResults
            results={autocompleteResults}
            loading={autocompleteLoading}
            onSelect={handleAutocompleteSelect}
            getFlagImage={getFlagImage}
          />
        )}
      </div>
    </div>
  );
};

const AutocompleteResultsSkeleton: React.FC = () => (
  <div className="flex flex-col items-start space-y-1">
    {Array.from({ length: 4 }, (_, i) => (
      <div key={i} className="w-full h-12 p-4 bg-loading rounded-2xl"></div>
    ))}
  </div>
);

interface AutocompleteResultsProps {
  results: AutocompleteResult[];
  loading: boolean;
  onSelect: (result: AutocompleteResult) => void;
  getFlagImage: (countryName: string) => string;
}

const AutocompleteResults: React.FC<AutocompleteResultsProps> = ({
  results,
  loading,
  onSelect,
  getFlagImage,
}) => {
  if (!loading && results.length === 0) {
    return null;
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      className="absolute top-16 w-full bg-white rounded-2xl shadow-lg border border-indigo-100 overflow-hidden z-20"
    >
      {loading ? (
        <AutocompleteResultsSkeleton />
      ) : (
        results.slice(0, 4).map((result, index) => (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: index * 0.1 }}
            key={result.waytora_id}
            className="flex items-center justify-between py-3 px-4 hover:bg-indigo-50 cursor-pointer transition-all duration-300"
            onClick={() => onSelect(result)}
          >
            <div>
              <p className="font-medium text-indigo-950">{result.name}</p>
              <p className="text-xs font-light text-indigo-500">
                {result.address_components.country}
              </p>
            </div>
            <div className="flex items-center space-x-2">
              <img
                src={getFlagImage(result.address_components.country)}
                alt={result.address_components.country}
                className="w-6 h-6 rounded-full shadow-sm"
              />
            </div>
          </motion.div>
        ))
      )}
    </motion.div>
  );
};

export default DepartureLocationStep;
